import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../context/AuthContext';
import { NOTIFICATIONS_REFRESH_EVENT } from '../utils/supervisionSync';
import { useOpenRecord } from '../hooks/useRecordFocus';
import {
  AlertTriangle,
  Bell,
  CheckCheck,
  Clock3,
  Inbox,
  Loader2,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';

// Priority styling mirrors the full Notifications page so the two read as one system.
const PRIORITY_META = {
  urgent: { icon: ShieldAlert, rail: 'border-l-rose-500', iconBox: 'bg-rose-50 text-rose-600' },
  high: { icon: AlertTriangle, rail: 'border-l-amber-500', iconBox: 'bg-amber-50 text-amber-600' },
  medium: { icon: Bell, rail: 'border-l-sky-400', iconBox: 'bg-sky-50 text-sky-600' },
  low: { icon: Sparkles, rail: 'border-l-slate-300', iconBox: 'bg-slate-50 text-slate-500' },
};

const fmtTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return '';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

// Three streams, never mixed (backend services/notification_policy.py): what is
// waiting on me, news about my own work, and exceptions for the roles that own
// them. Only the first is counted on the badge.
const VIEWS = [
  { key: 'action', label: 'Needs action', countKey: 'action_unread', empty: 'Nothing is waiting on you' },
  { key: 'updates', label: 'Updates', countKey: 'updates_unread', empty: 'No updates on your work' },
  { key: 'alerts', label: 'Alerts', countKey: 'alerts_unread', empty: 'No alerts for your role' },
];

// The number on the bell: only what needs this person. Older servers send no
// `badge`, so fall back to the plain unread count rather than to zero.
export const badgeOf = (summary) => (summary?.badge ?? summary?.unread ?? 0);

const broadcastNotificationSummary = (summary) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notificationsUpdated', { detail: summary || {} }));
};

// One row: a headline and one fact line, and — when the row offers them — the
// decisions it can take in place. The row itself opens the record; the buttons
// stop that click so "Looks fine" never also navigates away.
const ACTION_TONES = {
  danger: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
  neutral: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
};

export const NotificationItem = ({ notif, onOpen, onAct, acting }) => {
  const priority = PRIORITY_META[notif.priority || 'medium'] || PRIORITY_META.medium;
  const PriorityIcon = priority.icon;
  const actions = notif.resolved_outcome ? [] : (notif.actions || []);
  const open = () => onOpen(notif);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
      className={`flex w-full cursor-pointer items-start gap-3 border-b border-l-4 border-slate-100 px-4 py-3 text-left transition-colors ${
        notif.is_read ? `bg-white hover:bg-slate-50 ${priority.rail}` : 'border-l-amber-500 bg-amber-50/60 hover:bg-amber-100/70'
      }`}
      data-testid={`notification-item-${notif.id}`}
    >
      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${priority.iconBox}`}>
        <PriorityIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-slate-900">{notif.title}</span>
          {!notif.is_read && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-amber-500" title="Unread" />}
        </span>
        <span className="mt-0.5 block truncate text-xs text-slate-600" title={notif.message}>{notif.message}</span>
        {notif.resolved_outcome && (
          <span className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
            <CheckCheck className="h-3 w-3" />
            {notif.resolved_outcome}{notif.resolved_by ? ` by ${notif.resolved_by}` : ''}
          </span>
        )}
        {actions.length > 0 && (
          <span className="mt-2 flex flex-wrap gap-1.5">
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                disabled={acting}
                onClick={(e) => { e.stopPropagation(); onAct(notif, a); }}
                className={`rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-50 ${ACTION_TONES[a.tone] || ACTION_TONES.neutral}`}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); open(); }}
              className="rounded-md px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50"
            >
              {notif.action_label || 'Open'}
            </button>
          </span>
        )}
        <span className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
          <Clock3 className="h-3 w-3" />
          {fmtTime(notif.refreshed_at || notif.created_at)}
          {notif.occurrences > 1 && (
            <span title="Repeats of this alert are folded into this one row">· seen {notif.occurrences}×</span>
          )}
        </span>
      </span>
    </div>
  );
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [summary, setSummary] = useState({});
  const [view, setView] = useState('action');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);
  const wrapperRef = useRef(null);

  // Unread count: polled on mount/focus, refreshed every 60s, and updated in
  // place whenever the Notifications page broadcasts a new summary.
  useEffect(() => {
    if (!user) {
      setUnread(0);
      return undefined;
    }

    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API}/notifications/summary`);
        if (!cancelled) {
          setUnread(badgeOf(res.data));
          setSummary(res.data || {});
          broadcastNotificationSummary(res.data || {});
        }
      } catch (_err) {
        if (!cancelled) setUnread(0);
      }
    };

    const handleUpdated = (event) => setUnread(badgeOf(event.detail));

    fetchSummary();
    window.addEventListener('focus', fetchSummary);
    window.addEventListener('notificationsUpdated', handleUpdated);
    // Something that addresses a notification to this user just changed (an
    // approval request held or decided) — re-read now rather than on the minute.
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, fetchSummary);
    const interval = window.setInterval(fetchSummary, 60000);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', fetchSummary);
      window.removeEventListener('notificationsUpdated', handleUpdated);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, fetchSummary);
      window.clearInterval(interval);
    };
  }, [user]);

  const fetchRecent = useCallback(async (which = view) => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/notifications`, { params: { status: 'active', view: which, limit: 15 } }),
        axios.get(`${API}/notifications/summary`),
      ]);
      const rows = Array.isArray(listRes.data) ? listRes.data : [];
      // Unread first: the count on the bell has to point at something visible, and
      // newest-first alone left an older unread row below the fold.
      setItems([...rows.filter((n) => !n.is_read), ...rows.filter((n) => n.is_read)]);
      setUnread(badgeOf(summaryRes.data));
      setSummary(summaryRes.data || {});
      broadcastNotificationSummary(summaryRes.data || {});
    } catch (_err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [view]);

  // Close on outside click / Escape — standard dropdown behaviour.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Opening lands on whichever stream has something unread, in priority order —
  // an empty "Needs action" tab over three unread updates reads as "nothing new".
  const togglePanel = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const first = VIEWS.find((v) => (summary[v.countKey] || 0) > 0)?.key || 'action';
      setView(first);
      fetchRecent(first);
    }
  };

  const switchView = (key) => {
    setView(key);
    fetchRecent(key);
  };

  const markRead = async (notif) => {
    try {
      await axios.post(`${API}/notifications/${notif.id}/read`);
      setItems((prev) => prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)));
      const countKey = VIEWS.find((v) => v.key === view)?.countKey;
      setSummary((prev) => ({ ...prev, [countKey]: Math.max(0, (prev[countKey] || 0) - 1) }));
      if (notif.needs_action) {
        setUnread((prev) => {
          const next = Math.max(0, prev - 1);
          broadcastNotificationSummary({ badge: next });
          return next;
        });
      }
    } catch (_err) {
      /* non-blocking */
    }
  };

  const markAllRead = async () => {
    const ids = items.filter((n) => !n.is_read).map((n) => n.id);
    if (!ids.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/notifications/bulk`, { action: 'mark_read', ids, status: 'active', view });
      await fetchRecent();
    } catch (_err) {
      /* non-blocking */
    } finally {
      setBusy(false);
    }
  };

  // A decision taken from the bell is made by the server with the same code the
  // record's own screen uses; the row then shows who decided and stops asking.
  const act = async (notif, action) => {
    setActing(notif.id);
    try {
      const { data } = await axios.post(`${API}/notifications/${notif.id}/act`, { action: action.key });
      setItems((prev) => prev.map((n) => (n.id === notif.id ? data : n)));
      toast.success(`${action.label} — recorded`);
      fetchRecent(view);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record that');
      fetchRecent(view);
    } finally {
      setActing(null);
    }
  };

  // A notification names the record it was raised about (`source_id`, plus the
  // printed code in `source_label`). Carry both into the link so the target page
  // scrolls to that row and flashes it, instead of leaving the reader to find a
  // lot number somewhere in a table.
  const openNotification = (notif) => {
    if (!notif.is_read) markRead(notif);
    setOpen(false);
    if (!notif.link) {
      navigate('/notifications');
      return;
    }
    openRecord({ path: notif.link, id: notif.source_id, label: notif.source_label });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={togglePanel}
        className="relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        aria-label={unread > 0 ? `Notifications (${unread} need your action)` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 min-w-[18px] animate-pulse rounded-full bg-rose-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white"
            data-testid="notification-bell-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">Notifications</span>
              {unread > 0 && (
                <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                  {unread} to act on
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={markAllRead}
              disabled={busy || !items.some((n) => !n.is_read)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          </div>

          <div className="flex gap-1 border-b border-slate-100 px-2 py-1.5" role="tablist">
            {VIEWS.map((v) => {
              const count = summary[v.countKey] || 0;
              const active = view === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => switchView(v.key)}
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                    active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`notification-view-${v.key}`}
                >
                  {v.label}
                  {count > 0 && (
                    <span className={`rounded-full px-1.5 text-[10px] font-bold ${
                      active ? 'bg-white text-slate-900'
                        : v.key === 'action' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                    }`}
                    >
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-400">
                <Inbox className="h-7 w-7" />
                <p className="text-sm">{VIEWS.find((v) => v.key === view)?.empty || 'You are all caught up'}</p>
              </div>
            ) : (
              items.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notif={notif}
                  onOpen={openNotification}
                  onAct={act}
                  acting={acting === notif.id}
                />
              ))
            )}
          </div>

          <div className="border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-center">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
